import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Menu, Stack, TextField, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { CustomButton } from '../../../../components/mui/CustomButton';
import CustomModal from '../../../../components/mui/CustomModal';
import CustomTypography from '../../../../components/mui/CustomTypography';
import {
  AddNewSpeaker,
  addSpeakersRequest,
  removeUnusedSpeakersRequest,
  updateSpeakersRequest,
} from '../../Editor/actions';
import { trackEvent } from '../../../../utils/ga';

const SpeakerMenu = React.memo(({ open, anchorEl, onClose, speakerLabels, onChange, onNewLabel, onUpdate }) => {
  const dispatch = useDispatch();
  const [speakerModal, setSpeakerModal] = useState(false);
  const [speakerName, setSpeakerName] = useState('');
  const [oldSpeakerName, setOldSpeakerName] = useState('');
  const [type, setType] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const { id } = useParams();
  const { loading } = useSelector((state) => state.updateSpeaker);
  const { data: slateData } = useSelector((state) => state.slateData);
  const handleSubmit = () => {
    if (type === 'add') {
      onNewLabel(speakerName?.trim());
    } else if (type === 'edit') {
      dispatch(
        updateSpeakersRequest({
          fileId: id,
          OldSpeakerName: oldSpeakerName,
          NewSpeakerName: speakerName,
          onUpdate: () => {
            const speakers =
              slateData
                ?.filter((i) => i.type === 'speaker' && i.speaker !== oldSpeakerName && i.speaker)
                .map((i) => i.speaker) || [];

            const removeDuplicate = new Set([...speakers, speakerName]);
            const removeUnusedParam = {
              fileId: id,
              SpeakerNames: Array.from(removeDuplicate).join(','),
              projectId: null,
            };
            onUpdate(oldSpeakerName, speakerName, dispatch(removeUnusedSpeakersRequest(removeUnusedParam)));
          },
        }),
      );
    }
    setSpeakerModal(false);
    setType('');
    setSpeakerName('');
    setOldSpeakerName('');

    onClose();
  };

  useEffect(() => {
    setIsDirty(false);
  }, [speakerModal]);
  const isSpeakerAlreadyExists =
    isDirty && speakerLabels.map((i) => i?.toLowerCase()).includes(speakerName?.toLowerCase());
  return (
    <Menu
      open={open}
      onClose={onClose}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      sx={{
        '& .MuiPaper-root': {
          maxHeight: '400px',
          width: '320px',
          background: '#ffffff',
          boxShadow: '0px 4px 4px rgb(0 0 0 / 25%)',
          border: '1px solid #ebebeb',
          borderRadius: '4px',
          position: 'absolute',
          zIndex: 1,
          overflowY: 'hidden',
        },
      }}
    >
      <div className="speaktoedit-header" tabIndex={null}>
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          width="100%"
          sx={{ cursor: 'pointer' }}
          onClick={() => {
            setType('add');
            setSpeakerModal(true);
          }}
        >
          <AddIcon />
          <Typography fullWidth className="fnt-16 font-weight-bold mb-0" fontWeight={600}>
            Create Speaker
          </Typography>
        </Stack>
        <CustomModal
          sx={{ width: '500px !important' }}
          dialogClassName="custom-modal"
          open={speakerModal}
          onClose={() => {
            setSpeakerModal(false);
            setType('');
            setOldSpeakerName('');
            setSpeakerName('');
          }}
        >
          <Box sx={{ p: 5 }}>
            <Typography sx={{ fontWeight: 600, fontSize: 18 }}>
              {type === 'add' ? 'Add New' : 'Edit'} Speaker
            </Typography>
            <Typography fontSize={'0.875rem'} sx={{ opacity: 0.75 }}>
              {type === 'add' ? 'Enter the name of the new Speaker' : 'Change the name of the speaker'}
            </Typography>
            <TextField
              label="Name"
              fullWidth
              sx={{ marginTop: 2 }}
              value={speakerName}
              autoFocus
              onChange={(e) => {
                setIsDirty(true);
                setSpeakerName(e.target.value);
              }}
            />
            {isSpeakerAlreadyExists && <p className="text-danger fnt-12 mt-1 ">Speaker Already Exists</p>}
            <Stack mt={2} alignItems={'center'}>
              <CustomButton
                variant="secondary"
                sx={{ width: 'fit-content' }}
                onClick={handleSubmit}
                disabled={speakerName?.trim()?.length === 0 || loading || isSpeakerAlreadyExists}
                loading={loading}
              >
                {type === 'add' ? 'Add Speaker' : 'Save Changes'}
              </CustomButton>
            </Stack>
          </Box>
        </CustomModal>
      </div>
      <div className="speaktoedit-body">
        <div className="speaktoedit-speak-main">
          <ul
            className="speaktoedit-wrap allSpeakers custom-scroll"
            style={{ overflow: 'auto', maxHeight: '130px', paddingBottom: '10px' }}
          >
            {speakerLabels.map((speaker, i) => {
              const spkCount = `speaker-${i}`;
              const clscolor = i % 2 === 0 ? 'colorSP1' : 'colorSP2';

              return (
                <li
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                  key={spkCount}
                  className="speaktoedit-list spklbl"
                  data-attr={spkCount}
                  onClick={() => onChange(speaker)}
                >
                  <Stack width="100%" gap={2} alignItems="center" direction="row">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div className={`avatar-icon-text ${clscolor}`}>{speaker.substr(0, 1).toUpperCase()}</div>
                      <CustomTypography size="small">{speaker}</CustomTypography>
                    </div>

                    <div className="edit-icon-wrapper">
                      <EditIcon
                        onClick={(e) => {
                          e.stopPropagation();
                          setType('edit');
                          setSpeakerModal(true);
                          setSpeakerName(speaker);
                          setOldSpeakerName(speaker);
                        }}
                        sx={{ mr: 1, fontSize: 17 }}
                      />
                    </div>
                  </Stack>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </Menu>
  );
});

const SlateSpeakerLabel = ({ element, onChange, onUpdate, allowEdit }) => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const { fontSize } = useSelector((state) => state.settings);
  const { word: searchedWord, mode, wordSearchedId } = useSelector((state) => state.findAndReplace);

  // console.log('element',fontSize)

  const speakerLabels = useSelector(
    (state) => {
      const allSpeakersData = state?.getAllSpeakers?.allSpeakers?.data?.value;
      const allSpeakersDetails = allSpeakersData ? JSON.parse(allSpeakersData) : {};
      return allSpeakersDetails?.Speakers?.map((x) => x.SpeakerName) || [];
    },
    (prev, next) => JSON.stringify(prev) === JSON.stringify(next),
  );

  const [anchorEl, setAnchorEl] = useState(null);
  const [isApplyToAll, setIsApplyToAll] = useState(false);

  const handleClick = useCallback((event) => {
    if (!allowEdit) return;
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleChange = useCallback(
    (label) => {
      isApplyToAll ? onUpdate(label) : onChange(label);
      handleClose();
    },
    [isApplyToAll, onChange, onUpdate, handleClose],
  );

  const handleNewLabel = useCallback(
    (newLabel) => {
      handleChange(newLabel);
      dispatch(
        AddNewSpeaker({
          Id: '7a34683e-5181-4971-905b-9228e71a5929',
          ProjectId: '2b0c349c-dced-4b37-acc1-8aee8ad710c6',
          FileId: id,
          SpeakerName: newLabel,
          UserId: '00000000-0000-0000-0000-000000000000',
        }),
      );
      trackEvent('speaker_added', {
        speaker_name: newLabel,
      });
      const addParam = {
        fileId: id,
        speakerName: newLabel,
        speakerNames: speakerLabels.join(','),
      };
      dispatch(addSpeakersRequest(addParam));
    },
    [dispatch, handleChange, id, speakerLabels],
  );

  const typographyStyles = useMemo(
    () => ({
      fontSize: `${Math.max(fontSize, 11)}px !important`,
      color: '#190041 !important',
      textTransform: 'capitalize !important',
      cursor: allowEdit ? 'pointer !important' : 'default !important',
      userSelect: 'none',
    }),
    [fontSize],
  );

  const getIconSize = (fontSize) => {
    if (fontSize <= 14) return 'small';
    if (fontSize > 14 && fontSize <= 20) return 'medium';
    return 'large';
  };

  const shouldHighlight = useMemo(() => {
    if (mode !== 'speaker' || !searchedWord || !searchedWord.length || !element.speaker) return false;

    // Split search term into individual words
    const searchWords = searchedWord[0]
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0);
    const lowerSpeaker = element.speaker.toLowerCase();
    const lowerSearchWords = searchWords.map((word) => word.toLowerCase());

    // Check if all search words are present in the speaker name
    return lowerSearchWords.every((searchWord) => lowerSpeaker.includes(searchWord));
  }, [mode, searchedWord, element.speaker]);

  // Check if this is the currently selected match
  const isCurrentMatch = useMemo(() => {
    return shouldHighlight && wordSearchedId === `${element.id}-speaker`;
  }, [shouldHighlight, wordSearchedId, element.id]);

  // Create highlighted speaker name
  const highlightedSpeakerName = useMemo(() => {
    if (!shouldHighlight || !element.speaker) return element.speaker;

    const speaker = element.speaker;
    const searchWords = searchedWord[0]
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0);

    // First, try to find the complete search phrase as a whole
    const completeSearchPhrase = searchedWord[0].trim();
    const escapedCompletePhrase = completeSearchPhrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const completePhraseRegex = new RegExp(`(${escapedCompletePhrase})`, 'gi');

    // Check if the complete phrase exists in the speaker name
    if (speaker.toLowerCase().includes(completeSearchPhrase.toLowerCase())) {
      const parts = speaker.split(completePhraseRegex);

      return (
        <span>
          {parts.map((part, index) => {
            const isMatch = part.toLowerCase() === completeSearchPhrase.toLowerCase();

            if (isMatch) {
              return (
                <span key={index} style={{ backgroundColor: isCurrentMatch ? 'yellow' : 'grey' }}>
                  {part}
                </span>
              );
            }
            return part;
          })}
        </span>
      );
    }

    // Fallback to individual word highlighting if complete phrase not found
    const escapedWords = searchWords.map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const combinedRegex = new RegExp(`(${escapedWords.join('|')})`, 'gi');

    // Split the speaker name by the regex to get parts and matches
    const parts = speaker.split(combinedRegex);

    return (
      <span>
        {parts.map((part, index) => {
          // Check if this part is one of the search words (case-insensitive)
          const isMatch = searchWords.some((word) => part.toLowerCase() === word.toLowerCase());

          if (isMatch) {
            return (
              <span key={index} style={{ backgroundColor: isCurrentMatch ? 'yellow' : 'grey' }}>
                {part}
              </span>
            );
          }
          return part;
        })}
      </span>
    );
  }, [shouldHighlight, element.speaker, searchedWord, isCurrentMatch]);

  return (
    <Box sx={{ position: 'relative' }} contentEditable={false}>
      <CustomTypography
        component="p"
        size="mediumSmall"
        weight="semibold"
        id={`menu-${element.id}`}
        onClick={handleClick}
        sx={typographyStyles}
      >
        {element && element?.speaker ? (
          highlightedSpeakerName
        ) : (
          <Box>
            <AddIcon fontSize={getIconSize(fontSize)} />
            <u id="openSpeakerModelU">Add Speaker</u>
          </Box>
        )}
      </CustomTypography>
      <SpeakerMenu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        speakerLabels={speakerLabels}
        onChange={handleChange}
        isApplyToAll={isApplyToAll}
        setIsApplyToAll={setIsApplyToAll}
        onNewLabel={handleNewLabel}
        onUpdate={onUpdate}
      />
    </Box>
  );
};

export default React.memo(SlateSpeakerLabel);
